import React, { useEffect, useRef, useState } from 'react';
import { Measurement, MeasurementDraft, OutfitType } from '../types';
import { MEASUREMENT_FIELDS } from '../constants';
import { TrashIcon, BanknoteIcon, ChevronDownIcon } from './icons';
import { Image } from './Image';

interface MeasurementFormProps {
  onSave: (measurement: MeasurementDraft) => void;
  onClose: () => void;
  existingMeasurement?: Measurement;
  onUploadAttachment?: (file: File) => Promise<string>;
  onDeleteAttachment?: (fileUrl: string) => Promise<void>;
}

export const MeasurementForm: React.FC<MeasurementFormProps> = ({
  onSave,
  onClose,
  existingMeasurement,
  onUploadAttachment,
  onDeleteAttachment,
}) => {
  const MAX_VALUES_PER_MEASUREMENT = 3;
  const [outfitType, setOutfitType] = useState<OutfitType>(existingMeasurement?.outfitType || OutfitType.Haut);
  const [measurements, setMeasurements] = useState<Record<string, string[]>>({});
  const [notes, setNotes] = useState(existingMeasurement?.notes || '');
  const [attachments, setAttachments] = useState<string[]>(existingMeasurement?.attachments ?? []);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [sessionUploads, setSessionUploads] = useState<string[]>([]);
  const [customFields, setCustomFields] = useState<{ label: string; key: string }[]>([]);
  const [isAddingField, setIsAddingField] = useState(false);
  const [newFieldLabel, setNewFieldLabel] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // New Order states
  const [includeOrder, setIncludeOrder] = useState(false);
  const [orderAmount, setOrderAmount] = useState('');
  const [orderDueDate, setOrderDueDate] = useState('');
  const [orderIsPaid, setOrderIsPaid] = useState(false);

  const normalizeMeasurementValue = (value: unknown): string[] => {
    if (Array.isArray(value)) {
      return value
        .slice(0, MAX_VALUES_PER_MEASUREMENT)
        .map((entry) => String(entry).trim())
        .filter((entry) => entry !== '');
    }

    if (typeof value === 'string' || typeof value === 'number') {
      const text = String(value).trim();
      return text !== '' ? [text] : [];
    }

    return [];
  };

  const slugify = (text: string) => {
    return text
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]/g, ' ')
      .trim()
      .split(/\s+/)
      .map((word, index) => (index === 0 ? word : word.charAt(0).toUpperCase() + word.slice(1)))
      .join('');
  };

  useEffect(() => {
    if (existingMeasurement) {
      const fields = MEASUREMENT_FIELDS[existingMeasurement.outfitType];
      const mapped: Record<string, string[]> = {};
      fields.forEach((field) => {
        mapped[field.key] = normalizeMeasurementValue(existingMeasurement[field.key]);
      });

      // Detect custom fields
      const reservedKeys = new Set(['id', 'outfitType', 'date', 'notes', 'updatedAt', 'isDeleted', 'attachments']);
      const standardKeys = new Set(fields.map((f) => f.key));
      const detectedCustom: { label: string; key: string }[] = [];

      Object.keys(existingMeasurement).forEach((key) => {
        if (!reservedKeys.has(key) && !standardKeys.has(key)) {
          const val = existingMeasurement[key as keyof Measurement];
          if (typeof val === 'string' || typeof val === 'number' || Array.isArray(val)) {
            const label = key.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase());
            detectedCustom.push({ label, key });
            mapped[key] = normalizeMeasurementValue(val);
          }
        }
      });

      setMeasurements(mapped);
      setCustomFields(detectedCustom);
      setNotes(existingMeasurement.notes || '');
      setOutfitType(existingMeasurement.outfitType);
      setAttachments(existingMeasurement.attachments ?? []);
      setSessionUploads([]);
    } else {
      setMeasurements({});
      setCustomFields([]);
      setNotes('');
      setOutfitType(OutfitType.Haut);
      setAttachments([]);
      setSessionUploads([]);
    }
  }, [existingMeasurement]);

  useEffect(() => {
    return () => {
      if (sessionUploads.length && onDeleteAttachment) {
        sessionUploads.forEach((url) => {
          onDeleteAttachment(url).catch(() => undefined);
        });
      }
    };
  }, [sessionUploads, onDeleteAttachment]);

  const handleMeasurementChange = (key: string, index: number, value: string) => {
    setMeasurements((prev) => {
      const current = [...(prev[key] ?? [])];
      while (current.length < MAX_VALUES_PER_MEASUREMENT) {
        current.push('');
      }
      current[index] = value;
      return { ...prev, [key]: current };
    });
  };

  const handleAttachmentUploadClick = () => {
    if (!onUploadAttachment) {
      setAttachmentError("L'upload d'image n'est pas disponible.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleAttachmentSelected = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) {
      return;
    }
    if (!onUploadAttachment) {
      setAttachmentError("L'upload d'image n'est pas disponible.");
      return;
    }

    setAttachmentError(null);
    setUploadingAttachment(true);
    try {
      const url = await onUploadAttachment(file);
      setAttachments((prev) => [...prev, url]);
      setSessionUploads((prev) => [...prev, url]);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Echec du telechargement de l'image.";
      setAttachmentError(message);
    } finally {
      setUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = async (url: string) => {
    if (!window.confirm('Supprimer cette image ?')) {
      return;
    }
    setAttachmentError(null);
    setUploadingAttachment(true);
    try {
      if (onDeleteAttachment) {
        await onDeleteAttachment(url);
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : "Echec de suppression de l'image.";
      setAttachmentError(message);
      setUploadingAttachment(false);
      return;
    }
    setAttachments((prev) => prev.filter((item) => item !== url));
    setSessionUploads((prev) => prev.filter((item) => item !== url));
    setUploadingAttachment(false);
  };

  const handleAddFieldToggle = () => {
    setIsAddingField(!isAddingField);
    setNewFieldLabel('');
  };

  const handleAddCustomField = () => {
    if (!newFieldLabel.trim()) return;
    const label = newFieldLabel.trim();
    const key = slugify(label);

    // Check if already exists in standard or custom
    const isStandard = MEASUREMENT_FIELDS[outfitType].some((f) => f.key === key);
    const isCustom = customFields.some((f) => f.key === key);

    if (isStandard || isCustom) {
      alert('Ce champ existe deja.');
      return;
    }

    setCustomFields((prev) => [...prev, { label, key }]);
    setMeasurements((prev) => ({ ...prev, [key]: [] }));
    setIsAddingField(false);
    setNewFieldLabel('');
  };

  const handleRemoveCustomField = (key: string) => {
    if (!window.confirm('Supprimer ce champ personnalise ?')) return;
    setCustomFields((prev) => prev.filter((f) => f.key !== key));
    setMeasurements((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const cleanupSessionUploads = async () => {
    if (!sessionUploads.length || !onDeleteAttachment) {
      return;
    }
    await Promise.all(
      sessionUploads.map((url) =>
        onDeleteAttachment(url).catch(() => {
          // ignore errors during cleanup
        })
      )
    );
    setSessionUploads([]);
  };

  const handleCancel = async () => {
    await cleanupSessionUploads();
    onClose();
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    const measurementData: MeasurementDraft = {
      id: existingMeasurement?.id,
      outfitType,
      notes,
      attachments,
    };

    MEASUREMENT_FIELDS[outfitType].forEach((field) => {
      const entries = (measurements[field.key] ?? [])
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
        .slice(0, MAX_VALUES_PER_MEASUREMENT);

      if (entries.length === 1) {
        measurementData[field.key] = entries[0];
      } else if (entries.length > 1) {
        measurementData[field.key] = entries;
      } else {
        measurementData[field.key] = '';
      }
    });

    customFields.forEach((field) => {
      const entries = (measurements[field.key] ?? [])
        .map((entry) => entry.trim())
        .filter((entry) => entry !== '')
        .slice(0, MAX_VALUES_PER_MEASUREMENT);

      if (entries.length === 1) {
        measurementData[field.key] = entries[0];
      } else if (entries.length > 1) {
        measurementData[field.key] = entries;
      } else {
        measurementData[field.key] = '';
      }
    });

    if (includeOrder && orderAmount) {
      measurementData.orderData = {
        amount: parseFloat(orderAmount) || 0,
        dueDate: orderDueDate || new Date().toISOString().split('T')[0],
        paid: orderIsPaid,
      };
    }

    onSave(measurementData);
    setSessionUploads([]);
  };

  const currentFields = MEASUREMENT_FIELDS[outfitType];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-md">
        <h2 className="text-xl font-bold p-4 bg-brand-primary text-white rounded-t-lg">
          {existingMeasurement ? 'Modifier les Mesures' : 'Nouvelles Mesures'}
        </h2>
        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
          <div>
            <label htmlFor="outfitType" className="block text-sm font-medium t-text-secondary">
              Type de tenue
            </label>
            <select
              id="outfitType"
              value={outfitType}
              onChange={(event) => {
                const nextType = event.target.value as OutfitType;
                setOutfitType(nextType);
                setMeasurements({});
              }}
              disabled={!!existingMeasurement}
              className="t-input mt-1"
            >
              {Object.values(OutfitType).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>

          <div className="mb-1 rounded-md border border-indigo-200 bg-indigo-50/70 px-3 py-2 text-[11px] text-indigo-800 dark:border-indigo-600/60 dark:bg-indigo-900/20 dark:text-indigo-200">
            <span className="font-semibold">M1</span> = mesure principale.
            <span className="ml-2 font-medium">M2/M3</span> = variantes optionnelles.
          </div>

          <div className="mb-2 flex gap-2 text-[10px]">
            <span className="rounded-full bg-indigo-100 px-2 py-0.5 font-semibold text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-200">
              Champ principal
            </span>
            <span className="rounded-full bg-slate-100 px-2 py-0.5 font-medium text-slate-600 dark:bg-slate-700/60 dark:text-slate-300">
              Champs secondaires
            </span>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {currentFields.map((field) => (
              <div key={field.key}>
                <label htmlFor={field.key} className="block text-sm font-semibold text-slate-700 dark:text-slate-100">
                  {field.label}
                </label>
                <div className="mt-1 space-y-2">
                  <input
                    id={field.key}
                    type="number"
                    step="0.1"
                    placeholder="Cm"
                    value={measurements[field.key]?.[0] ?? ''}
                    onChange={(event) => handleMeasurementChange(field.key, 0, event.target.value)}
                    className="t-input border-indigo-300 bg-white font-medium text-slate-800 placeholder:text-indigo-500 dark:border-indigo-500/60 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-indigo-300"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      id={`${field.key}-2`}
                      type="number"
                      step="0.1"
                      placeholder="Cm2"
                      value={measurements[field.key]?.[1] ?? ''}
                      onChange={(event) => handleMeasurementChange(field.key, 1, event.target.value)}
                      className="t-input border-slate-300 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-200 dark:placeholder:text-slate-400"
                    />
                    <input
                      id={`${field.key}-3`}
                      type="number"
                      step="0.1"
                      placeholder="Cm3"
                      value={measurements[field.key]?.[2] ?? ''}
                      onChange={(event) => handleMeasurementChange(field.key, 2, event.target.value)}
                      className="t-input border-slate-300 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-200 dark:placeholder:text-slate-400"
                    />
                  </div>
                </div>
                <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">Mesures secondaires (optionnelles)</p>
              </div>
            ))}
            {/* Custom Fields */}
            {customFields.map((field) => (
              <div key={field.key} className="relative">
                <div className="flex justify-between items-center">
                  <label htmlFor={field.key} className="block text-sm font-semibold text-brand-secondary">
                    {field.label}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRemoveCustomField(field.key)}
                    className="text-red-400 hover:text-red-600 p-1"
                    title="Supprimer ce champ"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                </div>
                <div className="mt-1 space-y-2">
                  <input
                    id={field.key}
                    type="number"
                    step="0.1"
                    placeholder="Cm"
                    value={measurements[field.key]?.[0] ?? ''}
                    onChange={(event) => handleMeasurementChange(field.key, 0, event.target.value)}
                    className="t-input border-indigo-300 bg-white font-medium text-slate-800 placeholder:text-indigo-500 dark:border-indigo-500/60 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-indigo-300"
                  />
                  <div className="grid grid-cols-2 gap-2">
                    <input
                      id={`${field.key}-2`}
                      type="number"
                      step="0.1"
                      placeholder="Cm2"
                      value={measurements[field.key]?.[1] ?? ''}
                      onChange={(event) => handleMeasurementChange(field.key, 1, event.target.value)}
                      className="t-input border-slate-300 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-200 dark:placeholder:text-slate-400"
                    />
                    <input
                      id={`${field.key}-3`}
                      type="number"
                      step="0.1"
                      placeholder="Cm3"
                      value={measurements[field.key]?.[2] ?? ''}
                      onChange={(event) => handleMeasurementChange(field.key, 2, event.target.value)}
                      className="t-input border-slate-300 bg-slate-50 text-[13px] text-slate-700 placeholder:text-slate-500 dark:border-slate-600 dark:bg-slate-700/30 dark:text-slate-200 dark:placeholder:text-slate-400"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="pt-2">
            {!isAddingField ? (
              <button
                type="button"
                onClick={handleAddFieldToggle}
                className="text-sm text-brand-secondary font-semibold flex items-center hover:text-brand-accent transition"
              >
                <span className="mr-1">+</span> Ajouter une mesure specifique
              </button>
            ) : (
              <div className="bg-gray-50 dark:bg-slate-700/50 p-3 rounded-lg border border-gray-200 dark:border-slate-600 animate-in fade-in slide-in-from-bottom-2">
                <label className="block text-xs font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">
                  Nom de la mesure (ex: Tour de mollet)
                </label>
                <div className="flex space-x-2">
                  <input
                    autoFocus
                    type="text"
                    value={newFieldLabel}
                    onChange={(e) => setNewFieldLabel(e.target.value)}
                    placeholder="Entrez le nom..."
                    className="t-input flex-1"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddCustomField();
                      }
                    }}
                  />
                  <button
                    type="button"
                    onClick={handleAddCustomField}
                    className="bg-brand-secondary text-white px-3 py-1 rounded-md text-sm hover:bg-brand-accent"
                  >
                    OK
                  </button>
                  <button
                    type="button"
                    onClick={handleAddFieldToggle}
                    className="text-gray-500 dark:text-gray-400 px-2 py-1 text-sm"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            )}
          </div>

          {!existingMeasurement && (
            <div className="border-t border-gray-100 pt-4 mt-2">
              <button
                type="button"
                onClick={() => setIncludeOrder(!includeOrder)}
                className={`flex w-full items-center justify-between p-3 rounded-xl transition-all ${
                  includeOrder ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800 border' : 'bg-gray-50 dark:bg-slate-700/30 border-transparent border'
                }`}
              >
                <div className="flex items-center gap-2">
                  <div className={`p-2 rounded-lg ${includeOrder ? 'bg-emerald-500 text-white' : 'bg-gray-200 dark:bg-slate-600 text-gray-500 dark:text-gray-400'}`}>
                    <BanknoteIcon className="h-4 w-4" />
                  </div>
                  <div className="text-left">
                    <p className={`text-sm font-bold ${includeOrder ? 'text-emerald-700 dark:text-emerald-400' : 't-text-primary'}`}>
                      Ajouter une commande
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Enregistrer immediatement un prix et une date</p>
                  </div>
                </div>
                <ChevronDownIcon className={`h-5 w-5 text-gray-400 transition-transform ${includeOrder ? 'rotate-180' : ''}`} />
              </button>

              {includeOrder && (
                <div className="mt-3 space-y-4 p-3 bg-emerald-50/50 dark:bg-emerald-900/10 rounded-xl border border-emerald-100 dark:border-emerald-800/50 animate-in fade-in zoom-in-95 duration-200">
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">
                      Montant negocie (FCFA)
                    </label>
                    <input
                      type="number"
                      value={orderAmount}
                      onChange={(e) => setOrderAmount(e.target.value)}
                      placeholder="Ex: 25000"
                      className="t-input w-full"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-400 mb-1">
                      Date de livraison prevue
                    </label>
                    <input
                      type="date"
                      value={orderDueDate}
                      onChange={(e) => setOrderDueDate(e.target.value)}
                      className="t-input w-full"
                    />
                  </div>
                  <div className="flex items-center justify-between p-2 rounded-lg bg-white dark:bg-slate-800 border border-emerald-100 dark:border-emerald-800/30">
                    <span className="text-sm font-medium text-emerald-800 dark:text-emerald-400">Deja paye ?</span>
                    <button
                      type="button"
                      onClick={() => setOrderIsPaid(!orderIsPaid)}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${
                        orderIsPaid ? 'bg-emerald-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          orderIsPaid ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <div>
            <label htmlFor="notes" className="block text-sm font-medium t-text-secondary">
              Notes additionnelles
            </label>
            <textarea
              id="notes"
              rows={3}
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              className="t-input mt-1"
            />
          </div>

          <div>
            <div className="flex items-center justify-between">
              <span className="block text-sm font-medium t-text-secondary">Images</span>
              <button
                type="button"
                onClick={handleAttachmentUploadClick}
                className="rounded-md bg-brand-secondary px-3 py-2 text-sm font-semibold text-white transition hover:bg-brand-accent disabled:opacity-60"
                disabled={!onUploadAttachment || uploadingAttachment}
              >
                {uploadingAttachment ? 'Envoi en cours...' : 'Ajouter une image'}
              </button>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleAttachmentSelected}
            />
            {attachmentError && <p className="mt-2 text-sm text-red-600">{attachmentError}</p>}
            {attachments.length > 0 ? (
              <div className="mt-3 grid grid-cols-2 gap-2">
                {attachments.map((url) => (
                  <div key={url} className="relative overflow-hidden rounded-lg border border-gray-200 dark:border-slate-700">
                    <Image src={url} alt="Illustration mesure" className="h-24 w-full object-cover" />
                    <button
                      type="button"
                      onClick={() => handleRemoveAttachment(url)}
                      className="absolute right-2 top-2 rounded-full bg-white/90 p-1 text-red-600 shadow hover:bg-white"
                      aria-label="Supprimer l'image"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm text-gray-500">
                {onUploadAttachment
                  ? "Aucune image ajoutee pour cette mesure."
                  : "L'upload d'image n'est pas disponible."}
              </p>
            )}
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="px-4 py-2 bg-gray-200 dark:bg-slate-700 text-gray-800 dark:text-slate-200 rounded-md hover:bg-gray-300 dark:hover:bg-slate-600"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-brand-secondary text-white rounded-md hover:bg-brand-accent"
            >
              Enregistrer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
